<script setup>
import { tv } from 'tailwind-variants';

/**
 * @typedef {object} LoaderProps
 * @property {('default'|'magic'|'neutral')} [variantColor='default'] - Color variant of the loader
 * @property {('sm'|'md')} [size='md'] - Size variant of the loader
 */

/**
 * @type {LoaderProps}
 */
const props = defineProps({
  variantColor: {
    type: String,
    default: 'default',
  },
  size: {
    type: String,
    default: 'md',
  },
});

const loaderRecipe = tv({
  slots: {
    root: 'flex',
    firstDot: 'bg-brand-blue-11 rounded-full animate-dot',
    secondDot:
      'bg-brand-blue-11 rounded-full animate-dot [animation-delay:0.3s]',
    thirdDot:
      'bg-brand-blue-11 rounded-full animate-dot [animation-delay:0.6s]',
  },
  variants: {
    variantColor: {
      default: {
        firstDot: 'bg-brand-blue-11',
        secondDot: 'bg-brand-blue-11',
        thirdDot: 'bg-brand-blue-11',
      },
      magic: {
        firstDot: 'bg-brand-purple-12',
        secondDot: 'bg-brand-purple-12',
        thirdDot: 'bg-brand-purple-12',
      },
      neutral: {
        firstDot: 'bg-brand-gray-13 dark:bg-brand-gray-11',
        secondDot: 'bg-brand-gray-13 dark:bg-brand-gray-11',
        thirdDot: 'bg-brand-gray-13 dark:bg-brand-gray-11',
      },
    },
    size: {
      sm: {
        root: 'gap-[3px]',
        firstDot: 'w-1.5 h-1.5',
        secondDot: 'w-1.5 h-1.5',
        thirdDot: 'w-1.5 h-1.5',
      },
      md: {
        root: 'gap-1',
        firstDot: 'w-2 h-2',
        secondDot: 'w-2 h-2',
        thirdDot: 'w-2 h-2',
      },
    },
  },
  defaultVariants: {
    variantColor: 'default',
    size: 'md',
  },
});
</script>

<template>
  <div
    data-component="CsLoader"
    data-slot="root"
    :data-size="props.size"
    :data-variant-color="props.variantColor"
    :style="{
      '--bounce-size': props.size === 'md' ? '-2px' : '-1.5px',
    }"
    :class="loaderRecipe().root()"
  >
    <div data-slot="firstDot" :class="loaderRecipe(props).firstDot()" />
    <div data-slot="secondDot" :class="loaderRecipe(props).secondDot()" />
    <div data-slot="thirdDot" :class="loaderRecipe(props).thirdDot()" />
  </div>
</template>

<style>
@keyframes bounce-fade {
  0% {
    transform: translateY(0);
    opacity: 0.2;
  }
  50% {
    transform: translateY(var(--bounce-size));
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 0.2;
  }
}

.animate-dot {
  animation: bounce-fade 1.2s infinite ease-in-out;
}
</style>
